import { cloneDeep } from 'lodash-es';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFactoryNoLayout } from '../../../../../components/Form/FormFactory';
import { IDealDataModel } from '../../../../../data-models/deal.data-model';
import {
  DealFormViewModel,
  useDealFormSchema,
  createDealFormViewModel,
} from '../../../../../schemas/deal.schema';
import { FDMap } from '../../../../../util/data-structure/FDMap';
import { schemaToFormFields } from '../../../../../util/schema-utils';
import { createForm } from '../../../../../view-models/form.view-model';
import { UnstyledFieldset } from '../../../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import { FieldsWrapper, usePanelPagination } from '../../../../CompanyProfiles2/Summary/CompanyDetailsPanel';
import { Panel } from '../../../../CompanyProfiles2/Summary/Panel';
import { useDebouncedUpdateDeal } from '../../../hooks/useDealActions';
import { useLabelCreation } from '../../../hooks/useLabelCreation';
import { PanelPreferences } from '../../../../../data-models/preferences-v3.data-model';
import { MainDealPanelKeyType } from '../../../state/DealboardPreferences';
import { dealPanelFieldContainerCss } from './DealPanelStyles';

export interface IDealPanelProps {
  deal: IDealDataModel;
  editable?: boolean;
  preferences: PanelPreferences<MainDealPanelKeyType>;
}

export function MainDealPanel({ deal, preferences, editable = true }: IDealPanelProps) {
  const updateDeal = useDebouncedUpdateDeal();

  const methods = useForm<Partial<DealFormViewModel>>({
    defaultValues: { ...cloneDeep(createDealFormViewModel(deal as IDealDataModel) as DealFormViewModel) },
  });

  const onUpdateDeal = useCallback(
    async (payload: Partial<DealFormViewModel>) => {
      const { companyId, dealTypeId } = deal;
      updateDeal(deal, { companyId, dealTypeId, ...payload } as Partial<IDealDataModel>, false);
    },
    [deal, updateDeal]
  );

  return (
    <FormProvider {...methods}>
      <MainDealFieldsPanel
        onChange={onUpdateDeal}
        editable={editable}
        selectedFields={preferences.selectedFields}
      />
    </FormProvider>
  );
}

interface IMainDealFieldsProps {
  onChange: (data: Partial<DealFormViewModel>) => Promise<void>;
  editable?: boolean;
  selectedFields: MainDealPanelKeyType[];
  defaultPage?: number;
}
function MainDealFieldsPanel({
  onChange,
  editable = true,
  selectedFields,
  defaultPage,
}: IMainDealFieldsProps) {
  const { processLabels } = useLabelCreation();
  const dealFields = schemaToFormFields(useDealFormSchema().pick(selectedFields)).map((f) => ({
    ...f,
    disabled: !editable,
    required: false,
  }));

  const { paginationConfig, visibleFields } = usePanelPagination({
    fields: dealFields,
    initialPage: defaultPage,
  });

  const form = createForm<DealFormViewModel>({
    fields: FDMap.fromArray(visibleFields, 'key'),
    variant: 'form-inline',
  });

  const handleChange = useCallback(
    async (data: Partial<DealFormViewModel>) => {
      if (data.labels) {
        data.labels = await processLabels(data.labels);
      }
      onChange(data);
    },
    [onChange, processLabels]
  );

  return (
    <Panel title='Main Deal Details' paginationConfig={paginationConfig}>
      <UnstyledFieldset disabled={!editable} css={dealPanelFieldContainerCss}>
        <FieldsWrapper>
          <FormFactoryNoLayout form={form} onChange={handleChange} />
        </FieldsWrapper>
      </UnstyledFieldset>
    </Panel>
  );
}
